/**
 * THE FRAME LOOP.
 *
 * Reads `world` and the aim controller, writes pixels. It owns no game state
 * and decides nothing — a deer is where `poseAt` says it is, and it is dead
 * because the server said so.
 *
 * ── Budget ──────────────────────────────────────────────────────────────────
 * Per frame: one `drawImage` for the backdrop, one for the cover, a few dozen
 * path fills for the animals and effects. No `shadowBlur`, no gradients built
 * inside the loop, no allocation in the hot path beyond a couple of arrays.
 *
 * Device pixel ratio is capped at 2 (ROUNDTABLE.md, A-19). The target phone
 * reports 3, and rendering a full-screen animated scene at 3× is 2.25× the
 * fill rate for a difference nobody can resolve on a 6.9-inch panel.
 */

import { poseAt, type DeerSpawn } from '@shared/deer.js';
import { fitStage, HORIZON_SY, stageToPx, type StageFit } from '@shared/stage.js';
import { clock } from '../net/clock.js';
import { DEAD_LIFE_MS, FLOATER_LIFE_MS, TRACER_LIFE_MS, world } from './world.js';
import {
  drawCritter,
  drawCrosshair,
  drawDeer,
  drawDoe,
  drawGroundShadow,
  drawSkunk,
  hexA,
  paintBackdrop,
  paintCover,
  seatColor,
} from './art.js';
import type { AimController } from './aim.js';

const MAX_DPR = 2;

interface Weather {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
}

export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private raf = 0;
  private dpr = 1;
  private viewW = 0;
  private viewH = 0;
  private fit: StageFit = { x: 0, y: 0, w: 0, h: 0 };

  private backdrop: HTMLCanvasElement | null = null;
  private cover: HTMLCanvasElement | null = null;
  private backdropKey = '';

  private weather: Weather[] = [];
  private lastFrame = 0;
  /** Rolling frame time, ms. Surfaced in the diagnostics overlay. */
  fps = 60;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly aim: AimController,
  ) {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('canvas 2d unavailable');
    this.ctx = ctx;
    this.resize();
  }

  start() {
    if (this.raf) return;
    this.lastFrame = performance.now();
    const loop = () => {
      this.raf = requestAnimationFrame(loop);
      this.frame();
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  /** The stage rectangle in CSS pixels — the aim controller needs it for touch. */
  stage(): StageFit {
    return this.fit;
  }

  resize() {
    const rect = this.canvas.getBoundingClientRect();
    this.viewW = Math.max(1, Math.round(rect.width));
    this.viewH = Math.max(1, Math.round(rect.height));
    this.dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);
    this.canvas.width = Math.round(this.viewW * this.dpr);
    this.canvas.height = Math.round(this.viewH * this.dpr);
    this.fit = fitStage(this.viewW, this.viewH);
    this.backdropKey = '';
    this.buildMuzzle();
    this.seedWeather();
  }

  /**
   * Weather particles.
   *
   * The mist here is the second attempt. The first scattered forty semi-opaque
   * ellipses over the whole viewport, and the screenshot pass showed exactly
   * what that is: not mist, but tan blobs lying all over the ground and the
   * treeline like spilled paint. Real ground mist sits in a BAND at the
   * treeline, is very wide, very flat, and barely there. So: fewer of them,
   * ten times wider, confined to a horizon band, and at a third the alpha.
   */
  private seedWeather() {
    const kind = world.site.palette.weather;
    // Dust went from 26 big discs to 70 small ones: the same amount of ink,
    // spread over enough particles to read as grit in the air rather than as
    // a handful of objects.
    const count = kind === 'none' ? 0 : kind === 'snow' ? 90 : kind === 'mist' ? 14 : 70;
    const horizonPx = stageToPx(this.fit, 0, HORIZON_SY).y;
    const bandTop = horizonPx - this.viewH * 0.025;
    // A tenth of the viewport, not a fifth. At 0.22 the band reached well past
    // the treeline and onto open ground, where a flat pale oval stops reading
    // as mist and starts reading as a puddle. Ground mist hugs the trees.
    const bandH = this.viewH * 0.1;

    /**
     * Dust, second attempt, for the same reason the mist needed one.
     *
     * It was 6–18 px translucent circles scattered over the FULL viewport, and
     * the screenshot pass showed what that actually is: soft pale discs
     * floating in the sky, which reads as lens bokeh — a camera artefact, in a
     * game with no camera. Dust hangs low, is small, and moves sideways fast,
     * so it is confined to the ground plane and given a tenth the area.
     */
    const dustTop = stageToPx(this.fit, 0, HORIZON_SY + 0.16).y;

    this.weather = [];
    for (let i = 0; i < count; i++) {
      this.weather.push({
        x: Math.random() * this.viewW,
        y:
          kind === 'mist'
            ? bandTop + Math.random() * bandH
            : kind === 'dust'
              ? dustTop + Math.random() * (this.viewH - dustTop)
              : Math.random() * this.viewH,
        vx: kind === 'mist' ? 5 + Math.random() * 10 : kind === 'dust' ? 28 + Math.random() * 46 : (Math.random() - 0.5) * 18,
        vy: kind === 'snow' ? 22 + Math.random() * 34 : kind === 'dust' ? -3 + Math.random() * 6 : 2 + Math.random() * 6,
        r:
          kind === 'snow'
            ? 1 + Math.random() * 2.4
            : kind === 'mist'
              ? this.viewW * (0.18 + Math.random() * 0.3)
              : 1 + Math.random() * 2.6,
      });
    }
  }

  private ensureBackdrop() {
    const key = `${world.site.id}:${this.viewW}x${this.viewH}`;
    if (key === this.backdropKey && this.backdrop) return;
    this.backdropKey = key;
    const horizonPx = stageToPx(this.fit, 0, HORIZON_SY).y;
    // Painted at the FULL viewport size, not the stage size: the sky and
    // treeline continue past the stage so a tall phone has no bars, while
    // everything that can be shot stays inside the stage every player shares.
    this.backdrop = paintBackdrop(this.viewW, this.viewH, world.site.palette, world.site.cover, horizonPx, world.site.id);
    // Tall enough to actually fill the strip between the nearest deer and the
    // bottom of the screen. `0.62` in stage terms sits below every depth band a
    // wave spawns into, so cover never hides an animal.
    const coverH = Math.max(80, this.viewH - stageToPx(this.fit, 0, 0.58).y);
    this.cover = paintCover(this.viewW, coverH, world.site.palette, world.site.cover, world.site.id);
    this.seedWeather();
  }

  // ── the frame ────────────────────────────────────────────────────────────

  private frame() {
    const now = performance.now();
    const dtMs = Math.min(64, now - this.lastFrame);
    this.lastFrame = now;
    const dt = dtMs / 1000;
    this.fps += ((1000 / Math.max(1, dtMs)) - this.fps) * 0.08;

    world.step(dt);
    this.ensureBackdrop();

    const ctx = this.ctx;
    const t = clock.now();

    ctx.save();
    ctx.scale(this.dpr, this.dpr);

    /**
     * Camera kick. The shake is applied to the whole scene INCLUDING the
     * crosshair, because the recoil moves the gun and the gun is what the
     * crosshair is attached to. Damped to nothing under `prefers-reduced-motion`
     * by `world.kick` refusing to accumulate.
     */
    const shake = world.shake;
    if (shake > 0) {
      ctx.translate(
        (Math.random() - 0.5) * shake * this.viewH,
        (Math.random() - 0.5) * shake * this.viewH,
      );
    }

    if (this.backdrop) ctx.drawImage(this.backdrop, 0, 0, this.viewW, this.viewH);
    else {
      ctx.fillStyle = '#101418';
      ctx.fillRect(0, 0, this.viewW, this.viewH);
    }

    this.drawWeatherBack(dt);
    this.drawHerd(t);
    this.drawVarmints(t);

    if (this.cover) {
      ctx.drawImage(this.cover, 0, this.viewH - this.cover.height, this.viewW, this.cover.height);
    }

    this.drawWeatherFront(dt);
    this.drawEffects(now);
    this.drawFlash();
    this.drawVignette();
    this.drawSurround();

    ctx.restore();

    // The crosshair is drawn outside the shake transform's random jitter but
    // inside the dpr scale, so it stays crisp.
    ctx.save();
    ctx.scale(this.dpr, this.dpr);
    this.drawAim(t);
    ctx.restore();
  }

  // ── the herd ─────────────────────────────────────────────────────────────

  private drawHerd(t: number) {
    if (!world.active || world.kind === 'varmint') return;
    const ctx = this.ctx;
    const unit = this.fit.h / 2;

    // Painter's algorithm: furthest first, so a near deer occludes a far one.
    const live = world.liveDeer(t).slice();
    const poses = new Map<number, ReturnType<typeof poseAt>>();
    for (const d of live) poses.set(d.id, poseAt(d, t));
    live.sort((a, b) => (poses.get(b.id)!.z - poses.get(a.id)!.z));

    /**
     * Every shadow, then every animal.
     *
     * Not shadow-then-animal per deer: that puts a near deer's shadow on top of
     * a far deer's legs whenever the two overlap, which looks exactly like the
     * bug it is. Shadows are on the ground plane and the ground plane is behind
     * all of them.
     */
    for (const d of live) {
      const p = poses.get(d.id)!;
      // `p.sy` is the body's position and `p.lift` is how far a leap has taken
      // it off the ground, so the hooves' ground point is the sum.
      const g = stageToPx(this.fit, p.sx, p.sy + p.lift);
      drawGroundShadow(ctx, g.x, g.y, p.scale * unit, p.lift * unit);
    }

    for (const d of live) {
      const p = poses.get(d.id)!;
      const px = stageToPx(this.fit, p.sx, p.sy);
      const s = p.scale * unit;
      this.drawOne(d, px.x, px.y, s, p.dir as 1 | -1, p.gaitPhase, 0, null);
    }

    // The fallen, on top of the ground and under the cover.
    for (const [, dead] of world.dead) {
      const age = (t - dead.at) / DEAD_LIFE_MS;
      if (age < 0 || age > 1) continue;
      const px = stageToPx(this.fit, dead.sx, dead.sy);
      const s = dead.scale * unit;
      ctx.save();
      // A carcass keeps its shadow, fading out with the body.
      ctx.globalAlpha = 1 - Math.max(0, age - 0.75) * 4;
      drawGroundShadow(ctx, px.x, px.y, s, 0);
      ctx.globalAlpha = 1;
      if (dead.isDoe) {
        drawDoe(ctx, {
          x: px.x,
          y: px.y,
          s,
          dir: dead.dir as 1 | -1,
          gait: 0,
          palette: world.site.palette,
          dead: Math.min(1, age * 1.6),
          tint: null,
        });
      } else {
        drawDeer(ctx, {
          x: px.x,
          y: px.y,
          s,
          dir: dead.dir as 1 | -1,
          rack: 8,
          gait: 0,
          palette: world.site.palette,
          dead: Math.min(1, age * 1.6),
          tint: null,
        });
      }
      ctx.restore();
    }
  }

  private drawOne(
    d: DeerSpawn,
    x: number,
    y: number,
    s: number,
    dir: 1 | -1,
    gait: number,
    dead: number,
    tint: string | null,
  ) {
    const common = { x, y, s, dir, gait, palette: world.site.palette, dead, tint };
    if (d.kind === 'doe') drawDoe(this.ctx, common);
    else drawDeer(this.ctx, { ...common, rack: d.rack });
  }

  private drawVarmints(t: number) {
    if (!world.active || world.kind !== 'varmint') return;
    const ctx = this.ctx;
    const unit = this.fit.h / 2;
    const r = 0.085 * unit;

    for (const v of world.liveVarmints(t)) {
      const px = stageToPx(this.fit, v.sx, v.sy);
      const u = (t - v.t0) / (v.t1 - v.t0);
      if (v.kind === 'skunk') drawSkunk(ctx, px.x, px.y, r, u);
      else drawCritter(ctx, px.x, px.y, r, u, world.site.palette.animal);
    }

    // A brief puff where one was hit, so a clean shot is visible at speed.
    for (const [id, hit] of world.varmintDown) {
      const v = world.varmints.find((x) => x.id === id);
      if (!v) continue;
      const age = (t - hit.at) / 900;
      if (age > 1) continue;
      const px = stageToPx(this.fit, v.sx, v.sy);
      ctx.globalAlpha = 1 - age;
      ctx.fillStyle = hit.kind === 'skunk' ? '#7dbb6a' : '#d8c9a8';
      ctx.beginPath();
      ctx.arc(px.x, px.y - r * age * 2, r * (0.6 + age * 1.4), 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }
  }

  // ── effects ──────────────────────────────────────────────────────────────

  private drawEffects(now: number) {
    const ctx = this.ctx;

    for (const tr of world.tracers) {
      const age = (now - tr.born) / TRACER_LIFE_MS;
      if (age > 1) continue;
      const px = stageToPx(this.fit, tr.sx, tr.sy);
      const color = seatColor(tr.hue);
      const r = this.fit.h * (tr.hit ? 0.03 : 0.018) * (0.4 + age * 1.4);

      ctx.globalAlpha = (1 - age) * (tr.mine ? 1 : 0.75);
      if (tr.hit) {
        // A burst of sparks along the impact.
        ctx.strokeStyle = color;
        ctx.lineWidth = Math.max(1, this.fit.h * 0.004);
        ctx.beginPath();
        for (let i = 0; i < 7; i++) {
          const a = (i / 7) * Math.PI * 2 + tr.born;
          ctx.moveTo(px.x + Math.cos(a) * r * 0.4, px.y + Math.sin(a) * r * 0.4);
          ctx.lineTo(px.x + Math.cos(a) * r * 1.5, px.y + Math.sin(a) * r * 1.5);
        }
        ctx.stroke();
      } else {
        /**
         * A miss puts a hole in whatever it hit — and throws something up.
         *
         * The ring alone was a small, tidy, weightless mark; three expanding
         * puffs in the ground's own near colour give the shot somewhere to have
         * landed. Tinted from the palette so it is dust in the cornfield and
         * snow in the frost, without a per-site branch.
         */
        const puff = hexA(world.site.palette.groundNear, (1 - age) * 0.5);
        ctx.fillStyle = puff;
        for (let i = 0; i < 3; i++) {
          const a = tr.born * 0.7 + i * 2.1;
          ctx.beginPath();
          ctx.arc(
            px.x + Math.cos(a) * r * age * 1.1,
            px.y - age * r * 0.7 + Math.sin(a) * r * age * 0.5,
            r * (0.3 + age * 0.55),
            0,
            Math.PI * 2,
          );
          ctx.fill();
        }

        ctx.strokeStyle = 'rgba(0,0,0,0.55)';
        ctx.lineWidth = Math.max(1, this.fit.h * 0.005);
        ctx.beginPath();
        ctx.arc(px.x, px.y, r * 0.7, 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = hexA(color, 0.7);
        ctx.lineWidth = Math.max(1, this.fit.h * 0.0025);
        ctx.beginPath();
        ctx.arc(px.x, px.y, r * 0.7, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    // Floating score numbers.
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const f of world.floaters) {
      const age = (now - f.born) / FLOATER_LIFE_MS;
      if (age > 1) continue;
      const px = stageToPx(this.fit, f.sx, f.sy);
      const rise = age * this.fit.h * 0.1;
      const size = this.fit.h * (f.big ? 0.055 : 0.038);
      ctx.globalAlpha = age < 0.75 ? 1 : (1 - age) * 4;
      ctx.font = `900 ${size}px ui-sans-serif, system-ui, sans-serif`;
      ctx.lineWidth = size * 0.22;
      ctx.strokeStyle = 'rgba(0,0,0,0.8)';
      ctx.strokeText(f.text, px.x, px.y - rise);
      ctx.fillStyle = f.bad ? '#ff6b6b' : seatColor(f.hue);
      ctx.fillText(f.text, px.x, px.y - rise);
      ctx.globalAlpha = 1;
    }
  }

  /**
   * Muzzle flash.
   *
   * Two parts. The wash over the whole scene is the light the shot throws on
   * everything, and it is a flat fill rather than a gradient because a gradient
   * would have to be constructed every time the gun went off.
   *
   * The bloom at the bottom edge is the flash at the barrel, and it is the part
   * that makes the shot come from the player rather than from the screen. It is
   * built ONCE into an offscreen canvas at resize and blitted, so it costs a
   * `drawImage` and no allocation at all in the hot path.
   *
   * Both are rate-limited upstream by `world.pulse()` to three per second
   * (WCAG 2.3.1) and damped by `prefers-reduced-motion`.
   */
  private drawFlash() {
    if (world.flash <= 0.01) return;
    const ctx = this.ctx;
    ctx.globalCompositeOperation = 'lighter';
    ctx.fillStyle = `rgba(255,236,190,${0.26 * world.flash})`;
    ctx.fillRect(0, 0, this.viewW, this.viewH);
    if (this.muzzle) {
      ctx.globalAlpha = world.flash;
      ctx.drawImage(this.muzzle, 0, 0, this.viewW, this.viewH);
      ctx.globalAlpha = 1;
    }
    ctx.globalCompositeOperation = 'source-over';
  }

  private muzzle: HTMLCanvasElement | null = null;

  private buildMuzzle() {
    const c = document.createElement('canvas');
    c.width = Math.max(1, this.viewW);
    c.height = Math.max(1, this.viewH);
    const mc = c.getContext('2d')!;
    const cx = this.viewW / 2;
    const cy = this.viewH * 1.04;
    const r = Math.max(this.viewW, this.viewH) * 0.34;
    const g = mc.createRadialGradient(cx, cy, 0, cx, cy, r);
    g.addColorStop(0, 'rgba(255,244,214,0.85)');
    g.addColorStop(0.35, 'rgba(255,196,96,0.32)');
    g.addColorStop(1, 'rgba(255,150,40,0)');
    mc.fillStyle = g;
    mc.fillRect(0, 0, this.viewW, this.viewH);
    this.muzzle = c;
  }

  /**
   * The letterbox surround.
   *
   * The stage is a fixed 3:4 portrait box so that every player in a saloon is
   * looking at the same scene — that is a fairness requirement, not a layout
   * choice (shared/stage.ts). On a 16:9 monitor it leaves a wide margin either
   * side, which the backdrop already paints, so nothing is *missing* there.
   *
   * The problem is subtler than a black bar: without a boundary, a player on a
   * wide screen has no way to know that the treeline out at the edges is
   * scenery and no deer will ever run through it. So the margin is pushed down
   * and a hairline drawn where the stage ends — the same "you are looking out
   * of somewhere" reading the foreground cover gives vertically.
   */
  private drawSurround() {
    const left = this.fit.x;
    if (left < 8) return;
    const ctx = this.ctx;
    const right = this.fit.x + this.fit.w;

    ctx.fillStyle = 'rgba(6,5,4,0.55)';
    ctx.fillRect(0, 0, left, this.viewH);
    ctx.fillRect(right, 0, this.viewW - right, this.viewH);

    ctx.strokeStyle = 'rgba(201,162,39,0.22)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(left + 0.5, 0);
    ctx.lineTo(left + 0.5, this.viewH);
    ctx.moveTo(right - 0.5, 0);
    ctx.lineTo(right - 0.5, this.viewH);
    ctx.stroke();
  }

  private vignette: HTMLCanvasElement | null = null;
  private vignetteKey = '';

  private drawVignette() {
    const key = `${this.viewW}x${this.viewH}`;
    if (key !== this.vignetteKey) {
      this.vignetteKey = key;
      const c = document.createElement('canvas');
      c.width = this.viewW;
      c.height = this.viewH;
      const vc = c.getContext('2d')!;
      const g = vc.createRadialGradient(
        this.viewW / 2,
        this.viewH * 0.44,
        Math.min(this.viewW, this.viewH) * 0.32,
        this.viewW / 2,
        this.viewH * 0.44,
        Math.max(this.viewW, this.viewH) * 0.78,
      );
      g.addColorStop(0, 'rgba(0,0,0,0)');
      g.addColorStop(1, 'rgba(0,0,0,0.55)');
      vc.fillStyle = g;
      vc.fillRect(0, 0, this.viewW, this.viewH);
      this.vignette = c;
    }
    if (this.vignette) this.ctx.drawImage(this.vignette, 0, 0);
  }

  private drawWeatherBack(dt: number) {
    if (world.site.palette.weather !== 'mist') return;
    const ctx = this.ctx;
    // A third of the old alpha, and a sixth of the old vertical extent. Mist
    // you can see the treeline through is mist; anything denser is a blob.
    ctx.fillStyle = hexA(world.site.palette.haze, 0.1);
    for (const p of this.weather) {
      p.x += p.vx * dt;
      if (p.x - p.r > this.viewW) p.x = -p.r;
      ctx.beginPath();
      ctx.ellipse(p.x, p.y, p.r, Math.max(3, this.viewH * 0.012), 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private drawWeatherFront(dt: number) {
    const kind = world.site.palette.weather;
    if (kind !== 'snow' && kind !== 'dust') return;
    const ctx = this.ctx;
    ctx.fillStyle = kind === 'snow' ? 'rgba(255,255,255,0.85)' : 'rgba(226,206,158,0.5)';
    // Dust blows across; snow falls. Wrapping snow back to the top of the
    // screen is right; wrapping dust there would put grit in the sky.
    const top = kind === 'dust' ? stageToPx(this.fit, 0, HORIZON_SY + 0.16).y : -4;
    for (const p of this.weather) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      if (p.y > this.viewH || p.y < top) {
        p.y = kind === 'dust' ? top + Math.random() * (this.viewH - top) : top;
        p.x = Math.random() * this.viewW;
      }
      if (p.x > this.viewW) p.x = 0;
      else if (p.x < 0) p.x = this.viewW;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // ── the crosshair ────────────────────────────────────────────────────────

  private drawAim(t: number) {
    if (!world.active) return;
    // Not during the countdown: the site card is centred, and a crosshair
    // sitting under the big numeral just reads as clutter. There is nothing to
    // aim at yet anyway.
    if (t < world.startsAt - 250) return;
    const a = this.aim.state;
    const px = stageToPx(this.fit, a.sx, a.sy);
    const ready = world.shells > 0 && !world.reloadUntil && !world.benched;
    const r = this.fit.h * 0.045;

    // Benched: the sight goes dark and crossed out, so it is unmistakable that
    // the gun is not going to go off, rather than mysteriously silent.
    if (world.benched) {
      drawCrosshair(this.ctx, px.x, px.y, r, '#ff6b6b', false);
      const ctx = this.ctx;
      ctx.strokeStyle = '#ff6b6b';
      ctx.lineWidth = r * 0.2;
      ctx.beginPath();
      ctx.moveTo(px.x - r * 0.7, px.y - r * 0.7);
      ctx.lineTo(px.x + r * 0.7, px.y + r * 0.7);
      ctx.moveTo(px.x + r * 0.7, px.y - r * 0.7);
      ctx.lineTo(px.x - r * 0.7, px.y + r * 0.7);
      ctx.stroke();
      return;
    }

    drawCrosshair(this.ctx, px.x, px.y, r, ready ? '#fff3d6' : '#ffb36b', ready);

    // Reload arc, drawn around the crosshair rather than in the HUD — during a
    // wave the player's eyes are on the sight and nowhere else.
    if (world.reloadUntil) {
      const p = world.reloadProgress(t);
      const ctx = this.ctx;
      ctx.strokeStyle = 'rgba(0,0,0,0.6)';
      ctx.lineWidth = r * 0.3;
      ctx.beginPath();
      ctx.arc(px.x, px.y, r * 1.9, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * p);
      ctx.stroke();
      ctx.strokeStyle = '#ffd479';
      ctx.lineWidth = r * 0.16;
      ctx.beginPath();
      ctx.arc(px.x, px.y, r * 1.9, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * p);
      ctx.stroke();
    }
  }
}
